import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Sparkles,
  Users,
  Landmark,
  Gavel,
  Loader2,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import {
  getProject,
  listProjectProfiles,
  listProjectDocuments,
  runAnalysis,
} from "@/lib/profiles";
import { formatNumber } from "@/lib/documents";
import { pingFeature } from "@/lib/admin-api";

function n(v) {
  if (v === null || v === undefined || v === "") return null;
  const x = Number(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(x) && x > 0 ? x : null;
}

export default function WizardConfirm() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [populationActive, setPopulationActive] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [proj, profs, docs] = await Promise.all([
        getProject(projectId),
        listProjectProfiles(projectId),
        listProjectDocuments(projectId),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setProfiles(profs);
      setDocuments(docs);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les données.");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const analyzedDocs = useMemo(
    () => documents.filter((d) => d.status === "analyzed"),
    [documents]
  );

  const totalRevenue = useMemo(
    () =>
      analyzedDocs.reduce(
        (acc, d) => acc + (Number(d.extracted_data?.total_revenue) || 0),
        0
      ),
    [analyzedDocs]
  );
  const totalDebt = useMemo(
    () =>
      analyzedDocs.reduce(
        (acc, d) => acc + (Number(d.extracted_data?.public_debt) || 0),
        0
      ),
    [analyzedDocs]
  );

  const currency =
    analyzedDocs.find((d) => d.extracted_data?.currency)?.extracted_data?.currency ||
    "UM";

  const handleRun = async () => {
    if (profiles.length === 0) {
      toast.error("Configurez au moins un profil à l'étape 2.");
      return;
    }
    setRunning(true);
    try {
      const result = await runAnalysis(projectId, {
        profile_ids: profiles.map((p) => p.id),
        population_active: n(populationActive),
      });
      setAnalysis(result);
      toast.success("Analyse vitaliste terminée.");
      pingFeature("wizard.analysis.launched", {
        project_id: projectId,
        metadata: {
          profile_count: profiles.length,
          population_active: n(populationActive),
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du calcul vitaliste.");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="wizard-confirm-loading">
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="wizard-confirm-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 3/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Confirmation et lancement
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Vérifiez les paramètres ci-dessous, puis lancez le moteur de calcul
          vitaliste. <strong>Aucun appel LLM</strong> n'est effectué à cette
          étape&nbsp;: calculs 100% déterministes.
        </p>
      </div>

      <WizardStepper current={3} />
      <TutorialDeepLink
        chapter={4}
        section="section-4-1"
        id="wizard-step-3-confirm"
        label="Comprendre la formule vitaliste avant de lancer l'analyse"
        duration={7}
      />

      {/* Summary grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Project */}
        <Card data-testid="summary-project-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Gavel className="h-4 w-4 text-brand-gold" />
              Projet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Nom&nbsp;: </span>
              <strong>{project?.name}</strong>
            </div>
            {project?.country && (
              <div>
                <span className="text-muted-foreground">Pays&nbsp;: </span>
                {project.country}
              </div>
            )}
            {project?.fiscal_year && (
              <div>
                <span className="text-muted-foreground">Exercice&nbsp;: </span>
                {project.fiscal_year}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card data-testid="summary-documents-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Landmark className="h-4 w-4 text-brand-gold" />
              Documents analysés ({analyzedDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {analyzedDocs.length === 0 ? (
              <p className="text-muted-foreground">Aucun document analysé.</p>
            ) : (
              <>
                <div>
                  <span className="text-muted-foreground">Recettes cumulées&nbsp;: </span>
                  <strong className="font-mono">
                    {formatNumber(totalRevenue)} {currency}
                  </strong>
                </div>
                <div>
                  <span className="text-muted-foreground">Dette cumulée&nbsp;: </span>
                  <strong className="font-mono">
                    {formatNumber(totalDebt)} {currency}
                  </strong>
                </div>
                <ul className="mt-2 text-xs text-muted-foreground">
                  {analyzedDocs.slice(0, 3).map((d) => (
                    <li key={d.id}>· {d.filename}</li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>

        {/* Profiles */}
        <Card data-testid="summary-profiles-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-brand-gold" />
              Profils contribuables ({profiles.length}/3)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {profiles.length === 0 ? (
              <p className="text-muted-foreground">Aucun profil configuré.</p>
            ) : (
              <ul className="space-y-1">
                {profiles.map((p) => (
                  <li key={p.id} data-testid={`summary-profile-${p.id}`} className="flex items-center justify-between">
                    <span className="truncate font-medium">{p.name}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {formatNumber(p.monthly_salary)} {currency}/mois
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Optional: population_active for formula 3 */}
      <Card data-testid="optional-params-card" className="border-brand-gold/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Paramètre optionnel — conversion de la dette publique en vies futures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="pop-active" className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Population active (nombre de contribuables)
              </Label>
              <Input
                id="pop-active"
                type="number"
                min="1"
                className="mt-1 h-9"
                placeholder="Ex : 3 000 000"
                value={populationActive}
                onChange={(e) => setPopulationActive(e.target.value)}
                disabled={running}
                data-testid="population-active-input"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Laissez vide pour ne pas calculer la formule 3 (dette par
                contribuable en années de vie hypothéquées).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Launch card */}
      <Card
        className={analysis ? "border-green-500/30" : "border-brand-gold/30"}
        data-testid="launch-card"
      >
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          {!analysis && !running && (
            <>
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/50">
                <Sparkles className="h-8 w-8 text-brand-gold" />
              </div>
              <h2 className="text-2xl font-bold">Prêt à lancer l'analyse vitaliste</h2>
              <p className="max-w-xl text-sm text-muted-foreground">
                Le moteur va convertir chaque prélèvement et chaque ligne
                budgétaire en <strong>tranches de vies humaines</strong>, selon
                les 4 formules vitalistes.
              </p>
              <Button
                size="lg"
                onClick={handleRun}
                disabled={profiles.length === 0}
                className="h-12 px-8 text-base font-semibold"
                data-testid="launch-analysis-btn"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Lancer l'analyse vitaliste
              </Button>
            </>
          )}

          {running && (
            <div className="flex flex-col items-center gap-3" data-testid="analysis-running">
              <Loader2 className="h-10 w-10 animate-spin text-brand-gold" />
              <h2 className="text-xl font-bold">Calcul en cours…</h2>
              <p className="text-sm text-muted-foreground">
                Application des formules 1 à 4 sur {profiles.length} profil
                {profiles.length > 1 ? "s" : ""}.
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Durée habituelle&nbsp;: &lt; 1 seconde
              </div>
            </div>
          )}

          {analysis && (
            <div
              className="w-full max-w-2xl space-y-4 text-left"
              data-testid="analysis-result"
            >
              <div className="flex items-center justify-center gap-3 text-center">
                <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
                <div>
                  <h2 className="text-xl font-bold">Analyse terminée</h2>
                  <p className="text-sm text-muted-foreground">
                    {analysis.results?.summary?.profile_count || 0} profil
                    {(analysis.results?.summary?.profile_count || 0) > 1 ? "s" : ""}{" "}
                    traité
                    {(analysis.results?.summary?.profile_count || 0) > 1 ? "s" : ""}.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Quick preview of the key insights */}
              <div className="space-y-3">
                {analysis.results?.summary?.most_taxed_profile_name && (
                  <div
                    className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
                    data-testid="insight-most-taxed"
                  >
                    <div className="text-xs uppercase tracking-widest text-destructive/80">
                      Profil le plus lourdement prélevé
                    </div>
                    <div className="mt-1 text-sm">
                      <strong>{analysis.results.summary.most_taxed_profile_name}</strong>
                      {" — "}
                      <span className="font-mono">
                        {formatNumber(analysis.results.summary.most_taxed_days_per_year)} jours de vie
                      </span>
                      {" / an"}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(analysis.results?.profiles || []).map((p) => (
                    <div
                      key={p.profile_id}
                      className="rounded-md border border-border bg-card p-3"
                      data-testid={`result-profile-${p.profile_id}`}
                    >
                      <div className="text-sm font-semibold">{p.profile_name}</div>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-muted-foreground">Jours/an</div>
                          <div className="font-mono font-bold tabular-nums">
                            {formatNumber(p.individual?.jours_vie_sacrifies_par_an)}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">% vie fiscale</div>
                          <div className="font-mono font-bold tabular-nums">
                            {formatNumber(p.individual?.ratio_vie_fiscale_pct)}%
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Années/carrière</div>
                          <div className="font-mono font-bold tabular-nums">
                            {formatNumber(p.individual?.annees_vie_sur_carriere)}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Total prélevé</div>
                          <div className="font-mono font-bold tabular-nums">
                            {formatNumber(p.individual?.total_prelevements)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {analysis.results?.national?.available && (
                  <div
                    className="rounded-md border border-brand-gold/30 bg-accent/20 p-3"
                    data-testid="insight-national"
                  >
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Budget national en vies humaines
                    </div>
                    <div className="mt-1 text-sm">
                      Les recettes fiscales cumulées représentent{" "}
                      <strong className="font-mono">
                        {formatNumber(analysis.results.national.annees_vies_budget)}
                      </strong>{" "}
                      années de vie humaines consacrées à l'État.
                    </div>
                  </div>
                )}

                {analysis.results?.debt?.available && (
                  <div
                    className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
                    data-testid="insight-debt"
                  >
                    <div className="text-xs uppercase tracking-widest text-destructive/80">
                      Dette publique hypothéquée
                    </div>
                    <div className="mt-1 text-sm">
                      Chaque contribuable devra consacrer{" "}
                      <strong className="font-mono">
                        {formatNumber(analysis.results.debt.annees_vie_hypothequees)}
                      </strong>{" "}
                      années de sa vie pour rembourser sa part de dette publique.
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center text-xs italic text-muted-foreground">
                Le tableau de bord analytique et les graphiques seront
                disponibles au prochain cycle de développement.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/profiles`)
          }
          disabled={running}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <Button
          variant="default"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/results`)
          }
          disabled={running || !analysis}
          data-testid="wizard-finish-btn"
        >
          Voir le tableau de bord
        </Button>
      </div>
    </div>
  );
}
